import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY } from "../../utils/formatters";
import { tolerantId, fetchUsersByUserIds } from "../../utils/firestoreHelpers";
import {
  FaExclamationCircle,
  FaPlus,
  FaUser,
  FaCheck,
  FaBan,
  FaSearch,
  FaGavel,
  FaInfoCircle,
  FaMoneyBillWave,
} from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
// Tokens de diseño y componente compartido de estado
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, inputBase, pageTitle } from "../../utils/designTokens";
import { getAllActiveFunds } from "../../utils/fundHelpers";
import StatusBadge from "../../components/StatusBadge";
import LoadingState from "../../components/LoadingState";
import { useLatestRate } from "../../utils/exchangeRates";
import { formatMoney } from "../../utils/format";

const MultasAdmin = () => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const { rate: bcuRate, loading: bcuLoading } = useLatestRate();
  const buildingId = selectedBuilding?.buildingid;

  const [multas, setMultas] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("todas");
  const [search, setSearch] = useState("");

  // Anulacion modal
  const [annulModal, setAnnulModal] = useState(null); // multa object
  const [annulReason, setAnnulReason] = useState("");

  // Pago modal
  const [payModal, setPayModal] = useState(null);

  // Apelacion modal
  const [rejectAppealModal, setRejectAppealModal] = useState(null);
  const [rejectAppealReason, setRejectAppealReason] = useState("");

  const [form, setForm] = useState({
    unitId: "",
    userid: "",
    userType: "owner",
    amount: "",
    currency: "UYU",
    reason: "",
    targetFundId: "reserva",
    legalReference: "Art. 10, Ley 10.751",
  });

  // --- Fetch data ---
  useEffect(() => {
    if (!buildingId) return;
    fetchMultas();
    fetchApartments();
  }, [buildingId]);

  const fetchMultas = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "multas"),
        where("buildingid", "in", tolerantId(buildingId))
      );
      const snap = await getDocs(q);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setMultas(list);

      // Nombres: fichas del edificio (reglas 0D, C10: accessBids
      // array-contains). Se suman al mapa (fetchApartments carga los de las
      // unidades en paralelo). Best-effort: sin ellas la lista dice "ID n".
      const userIds = [...new Set(list.map((m) => m.userid).filter(Boolean))];
      if (userIds.length > 0) {
        try {
          const users = await fetchUsersByUserIds(userIds, buildingId);
          const map = {};
          users.forEach((u) => {
            map[String(u.userid)] = u.NombreCompleto || u.email || `ID ${u.userid}`;
          });
          setUsersMap((prev) => ({ ...prev, ...map }));
        } catch (usersErr) {
          console.warn("Multas: no se pudieron leer las fichas de los residentes:", usersErr?.code || usersErr?.message);
        }
      }
    } catch (err) {
      console.error("Error fetching multas:", err);
      showToast("Error al cargar multas", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const q = query(
        collection(db, "apartments"),
        where("building", "in", tolerantId(buildingId))
      );
      const snap = await getDocs(q);
      const apts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setApartments(apts);

      // Poblar usersMap con owners/tenants de los apartamentos
      const aptUserIds = [];
      apts.forEach((apt) => {
        [apt.owner, apt.ownerSecondary, apt.tenant, apt.tenantSecondary]
          .filter(Boolean)
          .forEach((id) => aptUserIds.push(id));
      });
      if (aptUserIds.length > 0) {
        const users = await fetchUsersByUserIds(aptUserIds, buildingId);
        setUsersMap((prev) => {
          const updated = { ...prev };
          users.forEach((u) => {
            updated[String(u.userid)] = u.NombreCompleto || u.email || `ID ${u.userid}`;
          });
          return updated;
        });
      }
    } catch (err) {
      console.error("Error fetching apartments:", err);
    }
  };

  // --- Get users for selected unit ---
  const getUnitUsers = () => {
    const apt = apartments.find((a) => a.id === form.unitId);
    if (!apt) return { owner: null, tenant: null };
    return {
      owner: apt.owner ? { id: apt.owner, label: `Propietario (${apt.owner})` } : null,
      tenant: apt.tenant ? { id: apt.tenant, label: `Inquilino (${apt.tenant})` } : null,
    };
  };

  // Auto-select user when unit changes
  useEffect(() => {
    if (!form.unitId) {
      setForm((f) => ({ ...f, userid: "", userType: "owner" }));
      return;
    }
    const users = getUnitUsers();
    if (form.userType === "owner" && users.owner) {
      setForm((f) => ({ ...f, userid: users.owner.id }));
    } else if (form.userType === "tenant" && users.tenant) {
      setForm((f) => ({ ...f, userid: users.tenant.id }));
    } else if (users.owner) {
      setForm((f) => ({ ...f, userid: users.owner.id, userType: "owner" }));
    } else {
      // La unidad no tiene owner ni tenant asignado
      setForm((f) => ({ ...f, userid: "", userType: "owner" }));
    }
  }, [form.unitId, form.userType, apartments]);

  // --- Create multa ---
  const handleCreate = async () => {
    if (!form.unitId || !form.userid || !form.amount || !form.reason) {
      showToast("Completá todos los campos obligatorios", "error");
      return;
    }
    const amount = Number(form.amount);
    if (amount <= 0) {
      showToast("El monto debe ser mayor a 0", "error");
      return;
    }

    setSaving(true);
    try {
      const apt = apartments.find((a) => a.id === form.unitId);
      await addDoc(collection(db, "multas"), {
        buildingid: Number(buildingId),
        unitId: form.unitId,
        unitNumber: apt?.number || "",
        userid: form.userid,
        userType: form.userType,
        amount,
        currency: form.currency || "UYU",
        reason: form.reason.trim(),
        status: "activa",
        targetFundId: form.targetFundId || "reserva",
        feedsReserveFund: form.targetFundId === "reserva",
        legalReference: form.legalReference || "Art. 10, Ley 10.751",
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.email || "",
        paidAt: null,
        annulledAt: null,
        annulledBy: null,
        annulledReason: null,
        includedInCierreId: null,
      });

      showToast("Multa creada correctamente", "success");
      setShowForm(false);
      resetForm();
      fetchMultas();
    } catch (err) {
      console.error("Error creating multa:", err);
      showToast("Error al crear multa", "error");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({
      unitId: "",
      userid: "",
      userType: "owner",
      amount: "",
      currency: "UYU",
      reason: "",
      targetFundId: "reserva",
      legalReference: "Art. 10, Ley 10.751",
    });
  };

  // --- Mark as paid ---
  const handleMarkPaid = async (multa) => {
    try {
      await updateDoc(doc(db, "multas", multa.id), {
        status: "pagada",
        paidAt: Timestamp.now(),
      });
      showToast("Multa marcada como pagada", "success");
      setPayModal(null);
      fetchMultas();
    } catch (err) {
      console.error("Error marking multa paid:", err);
      showToast("Error al actualizar multa", "error");
    }
  };

  // --- Annul multa ---
  const handleAnnul = async () => {
    if (!annulReason.trim()) {
      showToast("Ingresá el motivo de anulación", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "multas", annulModal.id), {
        status: "anulada",
        annulledAt: Timestamp.now(),
        annulledBy: auth.currentUser?.email || "",
        annulledReason: annulReason.trim(),
      });
      showToast("Multa anulada", "success");
      setAnnulModal(null);
      setAnnulReason("");
      fetchMultas();
    } catch (err) {
      console.error("Error annulling multa:", err);
      showToast("Error al anular multa", "error");
    }
  };

  // --- Aceptar apelación (anula la multa) ---
  const handleAcceptAppeal = async (multa) => {
    try {
      await updateDoc(doc(db, "multas", multa.id), {
        status: "anulada",
        annulledAt: Timestamp.now(),
        annulledBy: auth.currentUser?.email || "",
        annulledReason: `Apelación aceptada: ${multa.appealReason || ""}`,
        appealStatus: "accepted",
      });
      showToast("Apelación aceptada. Multa anulada.", "success");
      fetchMultas();
    } catch (err) {
      console.error("Error aceptando apelación:", err);
      showToast("Error al aceptar apelación", "error");
    }
  };

  // --- Rechazar apelación ---
  const handleRejectAppeal = async () => {
    if (!rejectAppealReason.trim()) {
      showToast("Ingresá el motivo del rechazo", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "multas", rejectAppealModal.id), {
        appealStatus: "rejected",
        appealRejectionReason: rejectAppealReason.trim(),
      });
      showToast("Apelación rechazada", "success");
      setRejectAppealModal(null);
      setRejectAppealReason("");
      fetchMultas();
    } catch (err) {
      console.error("Error rechazando apelación:", err);
      showToast("Error al rechazar apelación", "error");
    }
  };

  // --- Filter & search ---
  const filteredMultas = multas.filter((m) => {
    if (filter !== "todas" && m.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      const userName = usersMap[String(m.userid)] || "";
      const unitNum = m.unitNumber || "";
      if (
        !userName.toLowerCase().includes(s) &&
        !unitNum.toLowerCase().includes(s) &&
        !(m.reason || "").toLowerCase().includes(s)
      ) {
        return false;
      }
    }
    return true;
  });

  // --- Stats ---
  const stats = {
    total: multas.length,
    activas: multas.filter((m) => m.status === "activa").length,
    pagadas: multas.filter((m) => m.status === "pagada").length,
    anuladas: multas.filter((m) => m.status === "anulada").length,
    montoActivo: multas
      .filter((m) => m.status === "activa")
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0),
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaExclamationCircle size={48} color={colors.gray400} />
          <p style={{ color: colors.gray600, marginTop: 16 }}>Seleccioná un edificio para ver las multas</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>
            <FaGavel style={{ marginRight: 8 }} /> Multas
          </h2>
          <p style={styles.subtitle}>
            Gestión de multas del edificio — Art. 10, Ley 10.751
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowForm(true)}>
          <FaPlus size={14} /> Nueva Multa
        </button>
      </div>

      {/* Valores de referencia BCU */}
      {!bcuLoading && bcuRate && (bcuRate.ui || bcuRate.ur) && (
        <div style={{ ...cardBase, padding: spacing[4], marginBottom: spacing[4] }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: spacing[3] }}>
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
              Valores BCU ({bcuRate.date})
            </div>
            <div style={{ display: "flex", gap: spacing[6], flexWrap: "wrap" }}>
              {bcuRate.ui && <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>UI: <span style={{ color: colors.black }}>$ {bcuRate.ui.toFixed(4)}</span></span>}
              {bcuRate.ur && <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>UR: <span style={{ color: colors.black }}>$ {bcuRate.ur.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>}
              {bcuRate.sell && <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>USD: <span style={{ color: colors.black }}>$ {bcuRate.sell.toFixed(2)}</span></span>}
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={styles.kpiRow}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{stats.total}</div>
          <div style={styles.kpiLabel}>Total</div>
        </div>
        <div style={{ ...styles.kpiCard, borderLeftColor: colors.error }}>
          <div style={{ ...styles.kpiValue, color: colors.error }}>{stats.activas}</div>
          <div style={styles.kpiLabel}>Activas</div>
        </div>
        <div style={{ ...styles.kpiCard, borderLeftColor: colors.success }}>
          <div style={{ ...styles.kpiValue, color: colors.success }}>{stats.pagadas}</div>
          <div style={styles.kpiLabel}>Pagadas</div>
        </div>
        <div style={{ ...styles.kpiCard, borderLeftColor: colors.warning }}>
          <div style={{ ...styles.kpiValue, color: colors.warning }}>{formatCurrencyUY(stats.montoActivo)}</div>
          <div style={styles.kpiLabel}>Monto activo</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterRow}>
        <div style={styles.searchBox}>
          <FaSearch size={14} color={colors.gray400} />
          <input
            type="text"
            placeholder="Buscar por unidad, usuario o motivo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <div style={styles.filterTabs}>
          {[
            { key: "todas", label: "Todas" },
            { key: "activa", label: "Activas" },
            { key: "pagada", label: "Pagadas" },
            { key: "anulada", label: "Anuladas" },
            { key: "incluida_en_cierre", label: "En cierre" },
          ].map((f) => (
            <button
              key={f.key}
              style={{
                ...styles.filterTab,
                ...(filter === f.key ? styles.filterTabActive : {}),
              }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <LoadingState variant="block" message="Cargando multas…" />
      ) : filteredMultas.length === 0 ? (
        <div style={styles.emptyState}>
          <FaGavel size={48} color={colors.gray300} />
          <p style={{ color: colors.gray600, marginTop: 16 }}>
            {multas.length === 0 ? "No hay multas registradas" : "No hay multas que coincidan con el filtro"}
          </p>
        </div>
      ) : (
        <div style={styles.list}>
          {filteredMultas.map((m) => (
            <div key={m.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardLeft}>
                  <div style={styles.unitBadge}>
                    Unidad {m.unitNumber || "?"}
                  </div>
                  <StatusBadge status={m.status} />
                  {m.appealStatus === "pending" && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 400, background: "#fffbeb", color: "#b45309", border: "1px solid #fcd34d" }}>
                      <FaExclamationCircle size={10} /> Apelación pendiente
                    </span>
                  )}
                  {m.status !== "anulada" && (m.targetFundId || m.feedsReserveFund) && (
                    <span style={styles.reserveBadge} title={`Fondo: ${(getAllActiveFunds(selectedBuilding).find((f) => f.id === (m.targetFundId || (m.feedsReserveFund ? "reserva" : "caja")))?.name) || "Reserva"}`}>
                      <FaMoneyBillWave size={10} /> {(getAllActiveFunds(selectedBuilding).find((f) => f.id === (m.targetFundId || (m.feedsReserveFund ? "reserva" : "caja")))?.name) || "Reserva"}
                    </span>
                  )}
                </div>
                <div style={styles.cardAmount}>
                  {formatMoney(m.amount, m.currency || "UYU")}
                </div>
              </div>

              <div style={styles.cardBody}>
                <div style={styles.cardRow}>
                  <FaUser size={12} color={colors.gray600} />
                  <span style={styles.cardText}>
                    {usersMap[String(m.userid)] || `Usuario ${m.userid}`}
                    <span style={styles.cardSubtext}>
                      {" "}({m.userType === "tenant" ? "Inquilino" : "Propietario"})
                    </span>
                  </span>
                </div>
                <div style={styles.cardRow}>
                  <FaExclamationCircle size={12} color={colors.gray600} />
                  <span style={styles.cardText}>{m.reason}</span>
                </div>
                <div style={styles.cardRow}>
                  <FaInfoCircle size={12} color={colors.gray600} />
                  <span style={styles.cardSubtext}>
                    Creada: {formatDate(m.createdAt)}
                    {m.createdBy && ` por ${m.createdBy}`}
                    {m.legalReference && ` — Ref: ${m.legalReference}`}
                  </span>
                </div>
                {m.status === "pagada" && m.paidAt && (
                  <div style={styles.cardRow}>
                    <FaCheck size={12} color={colors.success} />
                    <span style={{ ...styles.cardSubtext, color: colors.success }}>
                      Pagada el {formatDate(m.paidAt)}
                    </span>
                  </div>
                )}
                {m.status === "anulada" && (
                  <div style={styles.cardRow}>
                    <FaBan size={12} color={colors.gray600} />
                    <span style={{ ...styles.cardSubtext, color: colors.gray600 }}>
                      Anulada el {formatDate(m.annulledAt)} por {m.annulledBy || "?"} — {m.annulledReason}
                    </span>
                  </div>
                )}
                {m.status === "incluida_en_cierre" && (
                  <div style={styles.cardRow}>
                    <FaCheck size={12} color={colors.info} />
                    <span style={{ ...styles.cardSubtext, color: colors.info }}>
                      Incluida en cierre mensual
                    </span>
                  </div>
                )}
                {/* Apelación del residente */}
                {m.appealStatus && (
                  <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 8, background: m.appealStatus === "pending" ? "#fffbeb" : m.appealStatus === "rejected" ? "#fef2f2" : "#f0fdf4", border: `1px solid ${m.appealStatus === "pending" ? "#fcd34d" : m.appealStatus === "rejected" ? "#fecaca" : "#bbf7d0"}` }}>
                    <div style={{ fontSize: 12, fontWeight: 400, color: m.appealStatus === "pending" ? "#b45309" : m.appealStatus === "rejected" ? "#dc2626" : "#16a34a", marginBottom: 4 }}>
                      {m.appealStatus === "pending" ? "Apelación pendiente de revisión" : m.appealStatus === "rejected" ? "Apelación rechazada" : "Apelación aceptada"}
                    </div>
                    <div style={{ fontSize: 13, color: colors.gray700 }}>{m.appealReason}</div>
                    {m.appealDate && <div style={{ fontSize: 11, color: colors.gray400, marginTop: 4 }}>Enviada el {formatDate(m.appealDate)} por {m.appealedBy || "residente"}</div>}
                    {m.appealAttachmentUrl && (
                      <a href={m.appealAttachmentUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: colors.primary, textDecoration: "none", marginTop: 4 }}>
                        <FaInfoCircle size={10} /> Ver adjunto del residente
                      </a>
                    )}
                    {m.appealStatus === "rejected" && m.appealRejectionReason && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626" }}>Motivo del rechazo: {m.appealRejectionReason}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions for activa */}
              {m.status === "activa" && (
                <div style={styles.cardActions}>
                  <button
                    style={styles.btnSuccess}
                    onClick={() => setPayModal(m)}
                  >
                    <FaCheck size={12} /> Marcar pagada
                  </button>
                  <button
                    style={styles.btnDanger}
                    onClick={() => { setAnnulModal(m); setAnnulReason(""); }}
                  >
                    <FaBan size={12} /> Anular
                  </button>
                  {m.appealStatus === "pending" && (
                    <>
                      <button
                        style={{ ...styles.btnSuccess, background: "#16a34a", borderColor: "#16a34a" }}
                        onClick={() => handleAcceptAppeal(m)}
                      >
                        <FaCheck size={12} /> Aceptar apelación
                      </button>
                      <button
                        style={styles.btnSecondary}
                        onClick={() => { setRejectAppealModal(m); setRejectAppealReason(""); }}
                      >
                        <FaBan size={12} /> Rechazar apelación
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create multa modal */}
      <AdminModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Nueva Multa"
        icon={<FaGavel />}
        size="medium"
        hideClose
        footer={
          <>
            <button style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setShowForm(false)}>
              Cancelar
            </button>
            <button style={styles.btnPrimary} className="habitia-btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? "Guardando..." : "Crear Multa"}
            </button>
          </>
        }
      >
            {/* Legal reference info */}
            <div style={styles.legalBox}>
              <FaGavel size={14} color={colors.warningDark} />
              <span>
                Ref: Art. 10, Ley 10.751 — Multas de 0,5% a 20% del valor catastral de la unidad.
                Las multas cobradas alimentan el fondo de reserva.
              </span>
            </div>

            <div style={styles.formGrid}>
              {/* Unit */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Unidad *</label>
                <select
                  style={styles.select}
                  value={form.unitId}
                  onChange={(e) => setForm({ ...form, unitId: e.target.value })}
                >
                  <option value="">Seleccionar unidad</option>
                  {apartments
                    .sort((a, b) => (a.number || "").localeCompare(b.number || "", undefined, { numeric: true }))
                    .map((apt) => (
                      <option key={apt.id} value={apt.id}>
                        {apt.number || apt.id} {apt.floor ? `(Piso ${apt.floor})` : ""}
                      </option>
                    ))}
                </select>
              </div>

              {/* User type */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Tipo de usuario *</label>
                <select
                  style={styles.select}
                  value={form.userType}
                  onChange={(e) => setForm({ ...form, userType: e.target.value })}
                >
                  <option value="owner">Propietario</option>
                  {(() => {
                    const users = getUnitUsers();
                    return users.tenant ? <option value="tenant">Inquilino</option> : null;
                  })()}
                </select>
              </div>

              {/* User display */}
              {form.userid && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Usuario</label>
                  <div style={styles.userDisplay}>
                    <FaUser size={12} color={colors.gray600} />
                    {usersMap[String(form.userid)] || `ID: ${form.userid}`}
                  </div>
                </div>
              )}

              {/* Currency + Amount */}
              <div style={{ display: "flex", gap: spacing[3] }}>
                <div style={{ ...styles.formGroup, flex: "0 0 160px" }}>
                  <label style={styles.label}>Moneda</label>
                  <select
                    style={styles.input}
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  >
                    <option value="UYU">UYU (Pesos)</option>
                    <option value="USD">USD (Dólares)</option>
                    <option value="UI">UI (U. Indexada)</option>
                    <option value="UR">UR (U. Reajustable)</option>
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.label}>Monto ({form.currency}) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={styles.input}
                    placeholder="Ej: 5000"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
              </div>
              {(form.currency === "UI" || form.currency === "UR" || form.currency === "USD") && bcuRate && (
                <div style={{ fontSize: typography.sizes.xs, color: colors.gray600, marginTop: -8, marginBottom: spacing[3] }}>
                  {form.currency === "USD"
                    ? `TC venta: 1 USD = $ ${bcuRate.sell?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : `Valor actual: 1 ${form.currency} = $ ${form.currency === "UI" ? bcuRate.ui?.toFixed(4) : bcuRate.ur?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  }
                  {form.amount && (() => {
                    const rate = form.currency === "UI" ? bcuRate.ui : form.currency === "UR" ? bcuRate.ur : bcuRate.sell;
                    return rate ? ` — Equivale a ~$ ${(Number(form.amount) * rate).toLocaleString("es-UY", { maximumFractionDigits: 0 })}` : "";
                  })()}
                  {form.currency !== "UYU" && (
                    <span style={{ display: "block", marginTop: 2, color: colors.gray500 }}>
                      La conversión final se realizará al TC del día anterior al cierre de mes.
                    </span>
                  )}
                </div>
              )}

              {/* Legal info about multas */}
              {form.unitId && (() => {
                const apt = apartments.find(a => a.id === form.unitId);
                const vc = Number(apt?.valorCatastral) || 0;
                return (
                  <div style={{ gridColumn: "1 / -1", background: colors.infoLight, border: `1px solid ${colors.info}40`, borderRadius: radius.sm, padding: "10px 14px", fontSize: typography.sizes.sm, color: colors.infoDark, lineHeight: 1.5 }}>
                    <strong>Referencia legal (Ley 10.751 Art. 10):</strong> Las multas judiciales pueden ser entre 0,5% y 20% del valor catastral de la unidad. Las multas del reglamento interno se definen por acuerdo de copropietarios.
                    {vc > 0 && (
                      <div style={{ marginTop: 6, fontWeight: 400 }}>
                        Valor catastral de la unidad: ${vc.toLocaleString("es-UY")} — Rango referencial: ${Math.round(vc * 0.005).toLocaleString("es-UY")} (0,5%) a ${Math.round(vc * 0.20).toLocaleString("es-UY")} (20%)
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Reason */}
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Motivo *</label>
                <textarea
                  style={{ ...styles.input, minHeight: 80, resize: "vertical" }}
                  placeholder="Describí el motivo de la multa..."
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                />
              </div>

              {/* Fondo destino */}
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Fondo destino</label>
                <select
                  style={styles.select}
                  value={form.targetFundId}
                  onChange={(e) => setForm({ ...form, targetFundId: e.target.value })}
                >
                  {getAllActiveFunds(selectedBuilding).map((fund) => (
                    <option key={fund.id} value={fund.id}>{fund.name}</option>
                  ))}
                </select>
              </div>

              {/* Legal reference */}
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Referencia legal</label>
                <input
                  type="text"
                  style={styles.input}
                  value={form.legalReference}
                  onChange={(e) => setForm({ ...form, legalReference: e.target.value })}
                />
              </div>
            </div>
      </AdminModal>

      {/* Pay confirmation modal */}
      <AdminModal
        open={!!payModal}
        onClose={() => setPayModal(null)}
        title="Confirmar pago"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setPayModal(null)}>
              Cancelar
            </button>
            <button style={styles.btnSuccess} className="habitia-btn-primary" onClick={() => handleMarkPaid(payModal)}>
              <FaCheck size={12} /> Confirmar pago
            </button>
          </>
        }
      >
            {payModal && (
              <>
                <p style={{ color: colors.gray900, margin: "16px 0" }}>
                  Marcar como pagada la multa de <strong>{formatCurrencyUY(payModal.amount)}</strong> a
                  la Unidad <strong>{payModal.unitNumber}</strong>?
                </p>
                {(payModal.targetFundId || payModal.feedsReserveFund) && (
                  <div style={styles.legalBox}>
                    <FaMoneyBillWave size={14} color={colors.warningDark} />
                    <span>Este monto se contabilizará como ingreso del fondo de {(getAllActiveFunds(selectedBuilding).find((f) => f.id === (payModal.targetFundId || "reserva"))?.name) || "reserva"}.</span>
                  </div>
                )}
              </>
            )}
      </AdminModal>

      {/* Annul modal */}
      <AdminModal
        open={!!annulModal}
        onClose={() => setAnnulModal(null)}
        title="Anular multa"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setAnnulModal(null)}>
              Cancelar
            </button>
            <button style={styles.btnDanger} onClick={handleAnnul}>
              <FaBan size={12} /> Anular multa
            </button>
          </>
        }
      >
            {annulModal && (
              <>
                <p style={{ color: colors.gray900, margin: "16px 0" }}>
                  Anular multa de <strong>{formatCurrencyUY(annulModal.amount)}</strong> a
                  la Unidad <strong>{annulModal.unitNumber}</strong>?
                </p>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Motivo de anulación *</label>
                  <textarea
                    style={{ ...styles.input, minHeight: 60, resize: "vertical" }}
                    placeholder="Motivo obligatorio..."
                    value={annulReason}
                    onChange={(e) => setAnnulReason(e.target.value)}
                  />
                </div>
              </>
            )}
      </AdminModal>

      {/* Rechazar apelación modal */}
      <AdminModal
        open={!!rejectAppealModal}
        onClose={() => setRejectAppealModal(null)}
        title="Rechazar apelación"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} className="habitia-btn-secondary" onClick={() => setRejectAppealModal(null)}>
              Cancelar
            </button>
            <button style={styles.btnDanger} onClick={handleRejectAppeal} disabled={!rejectAppealReason.trim()}>
              <FaBan size={12} /> Rechazar apelación
            </button>
          </>
        }
      >
            {rejectAppealModal && (
              <>
                <p style={{ color: colors.gray900, margin: "16px 0" }}>
                  Rechazar apelación de la multa de <strong>{formatCurrencyUY(rejectAppealModal.amount)}</strong> — Unidad <strong>{rejectAppealModal.unitNumber}</strong>
                </p>
                <div style={{ background: "#fffbeb", borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 13, color: "#b45309" }}>
                  <strong>Apelación del residente:</strong> {rejectAppealModal.appealReason}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Motivo del rechazo *</label>
                  <textarea
                    style={{ ...styles.input, minHeight: 60, resize: "vertical" }}
                    placeholder="Explicá por qué se rechaza la apelación..."
                    value={rejectAppealReason}
                    onChange={(e) => setRejectAppealReason(e.target.value)}
                  />
                </div>
              </>
            )}
      </AdminModal>
    </div>
  );
};

// ---- ESTILOS (migrados a designTokens) ----

const styles = {
  // Contenedor principal
  container: {
    padding: spacing[6],
    fontFamily: typography.fontFamily,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: spacing[5],
    flexWrap: "wrap",
    gap: spacing[3],
  },
  title: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },
  subtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: "4px 0 0",
  },

  // KPIs
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: spacing[3],
    marginBottom: spacing[5],
  },
  kpiCard: {
    background: colors.white,
    borderRadius: radius.md,
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderLeft: `4px solid ${colors.primary}`,
    boxShadow: shadows.sm,
  },
  kpiValue: {
    fontSize: typography.sizes["2xl"],
    fontWeight: 400,
    color: colors.primary,
  },
  kpiLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    marginTop: 2,
  },

  // Filtros y busqueda
  filterRow: {
    display: "flex",
    gap: spacing[3],
    marginBottom: spacing[4],
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "6px 12px",
    flex: "1 1 220px",
    maxWidth: 320,
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: typography.sizes.base,
    flex: 1,
    background: "transparent",
    fontFamily: typography.fontFamily,
  },
  filterTabs: {
    display: "flex",
    gap: spacing[1],
    flexWrap: "wrap",
  },
  filterTab: {
    padding: "6px 14px",
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    background: colors.white,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.gray900,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  filterTabActive: {
    background: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },

  // Lista de tarjetas
  list: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
  },
  card: {
    ...cardBase,
    padding: spacing[4],
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  cardLeft: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    flexWrap: "wrap",
  },
  unitBadge: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.black,
    background: colors.gray100,
    borderRadius: 6,
    padding: "2px 10px",
  },
  reserveBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    fontWeight: 400,
    color: colors.successDark,
    background: colors.successLight,
    borderRadius: radius.md,
    padding: "2px 8px",
  },
  cardAmount: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  cardText: {
    fontSize: typography.sizes.base,
    color: colors.gray900,
  },
  cardSubtext: {
    fontSize: typography.sizes.sm,
    color: colors.gray400,
  },
  cardActions: {
    display: "flex",
    gap: spacing[2],
    marginTop: spacing[3],
    paddingTop: spacing[3],
    borderTop: `1px solid ${colors.gray100}`,
  },

  // Estado vacio y spinner
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },
  // Botones
  btnPrimary: {
    ...btnPrimary,
    padding: "8px 18px",
    fontSize: typography.sizes.base,
  },
  btnSecondary: {
    ...btnSecondary,
    padding: "8px 18px",
    fontSize: typography.sizes.base,
  },
  btnSuccess: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    background: colors.success,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  btnDanger: {
    ...btnDanger,
    padding: "6px 14px",
    fontSize: typography.sizes.sm,
  },

  // Formulario
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: spacing[4],
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },
  label: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.gray900,
  },
  input: {
    ...inputBase,
    width: "auto",
    padding: "8px 12px",
    fontSize: typography.sizes.base,
  },
  select: {
    padding: "8px 12px",
    border: `1px solid ${colors.gray300}`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    outline: "none",
    background: colors.white,
    fontFamily: typography.fontFamily,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.base,
    color: colors.gray900,
    cursor: "pointer",
  },
  userDisplay: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "8px 12px",
    background: colors.gray50,
    borderRadius: radius.sm,
    fontSize: typography.sizes.base,
    color: colors.gray900,
  },
  legalBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    padding: "10px 14px",
    background: colors.warningLight,
    border: `1px solid ${colors.warning}40`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.sm,
    color: colors.warningDark,
    marginBottom: spacing[4],
    lineHeight: 1.5,
  },
};

export default MultasAdmin;
